import { Card, CardContent } from '@/components/ui/card';
import { TrendingUp, TrendingDown, Minus, Euro, Users, Calendar, BarChart3 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatEUR } from '@/lib/revenuePricing';

interface DeltaProps {
  value: number;
  invert?: boolean;
}

function Delta({ value, invert }: DeltaProps) {
  if (!Number.isFinite(value)) return null;
  const positive = invert ? value < 0 : value > 0;
  const negative = invert ? value > 0 : value < 0;
  const Icon = value === 0 ? Minus : value > 0 ? TrendingUp : TrendingDown;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 text-xs font-medium',
        positive && 'text-success',
        negative && 'text-destructive',
        value === 0 && 'text-muted-foreground'
      )}
    >
      <Icon className="h-3 w-3" />
      {value > 0 ? '+' : ''}
      {value}%
    </span>
  );
}

interface KpiCardsProps {
  mrr: { current: number; previous: number; deltaPct: number };
  arr: number;
  monthRevenue: { current: number; previous: number; deltaPct: number };
  activePayingCustomers: { current: number; previous: number; deltaPct: number };
}

export function KpiCards({ mrr, arr, monthRevenue, activePayingCustomers }: KpiCardsProps) {
  const tiles = [
    {
      label: 'MRR',
      value: formatEUR(mrr.current),
      sub: `ARR ${formatEUR(arr)}`,
      delta: mrr.deltaPct,
      icon: Euro,
      tone: 'primary' as const,
    },
    {
      label: 'Revenue This Month',
      value: formatEUR(monthRevenue.current),
      sub: `vs ${formatEUR(monthRevenue.previous)} last month`,
      delta: monthRevenue.deltaPct,
      icon: Calendar,
      tone: 'success' as const,
    },
    {
      label: 'Active Paying Customers',
      value: activePayingCustomers.current.toLocaleString('de-DE'),
      sub: `${activePayingCustomers.previous} last month`,
      delta: activePayingCustomers.deltaPct,
      icon: Users,
      tone: 'accent' as const,
    },
    {
      label: 'ARPU',
      value: activePayingCustomers.current > 0
        ? formatEUR(mrr.current / activePayingCustomers.current)
        : '—',
      sub: 'Avg revenue per user / month',
      icon: BarChart3,
      tone: 'muted' as const,
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {tiles.map((t) => {
        const Icon = t.icon;
        return (
          <Card key={t.label} className="app-tile">
            <CardContent className="p-5">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    {t.label}
                  </p>
                  <p className="text-2xl font-semibold tracking-tight mt-1.5">{t.value}</p>
                </div>
                <div className="h-9 w-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                  <Icon className="h-4 w-4" />
                </div>
              </div>
              <div className="flex items-center gap-2 mt-3">
                {t.delta !== undefined && <Delta value={t.delta} />}
                <span className="text-xs text-muted-foreground truncate">{t.sub}</span>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
